/**
 * Development movement fallbacks (owner: env-builder).
 *
 * `DevPlayerMover` drives the player from WASD/arrows + the abstract `input:dir` bus event when the
 * actor delivered by `@/characters` has no `tick(dtMs)` of its own. `DevFollowerTrail` gives
 * followers a simple path-history trail under the same condition. Both are replaced transparently
 * once the characters builder ships tickable actors (see docs/core-change-requests/env-builder.md).
 */
import Phaser from 'phaser';
import { bus, Events, type EventPayloads } from '@/core/events';
import type { IActor } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import { facingFrom, stepMove, type BlockedFn, type MoveInput } from './movement';

type KeyMap = Record<'W' | 'A' | 'S' | 'D' | 'UP' | 'DOWN' | 'LEFT' | 'RIGHT', Phaser.Input.Keyboard.Key>;
const CONFIRM_KEYS = ['keydown-ENTER', 'keydown-SPACE', 'keydown-Z'] as const;

export interface DevMoverOptions { speed: number; mapId: string; moveEmitMs?: number }

export class DevPlayerMover {
  private keys: KeyMap | null = null;
  private touch: MoveInput = { dx: 0, dy: 0 };
  private fx: number;
  private fy: number;
  private enabled = true;
  private walking = false;
  private emitAcc = 0;
  private readonly onDir = (p: EventPayloads[typeof Events.INPUT_DIR]) => { this.touch = { dx: p.x, dy: p.y }; };
  /** The characters InputMap is absent on this path, so confirm keys are bridged to the bus here. */
  private readonly onConfirmKey = (): void => { if (this.enabled) bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }); };

  constructor(private readonly scene: Phaser.Scene, private readonly actor: IActor, private readonly isBlocked: BlockedFn, private readonly opts: DevMoverOptions) {
    this.fx = actor.x;
    this.fy = actor.y;
    const kb = scene.input.keyboard;
    if (kb) {
      this.keys = kb.addKeys('W,A,S,D,UP,DOWN,LEFT,RIGHT') as KeyMap;
      for (const ev of CONFIRM_KEYS) kb.on(ev, this.onConfirmKey);
    }
    bus.onTyped(Events.INPUT_DIR, this.onDir);
  }

  get isEnabled(): boolean { return this.enabled; }
  get isWalking(): boolean { return this.walking; }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.touch = { dx: 0, dy: 0 };
      if (this.walking) { this.walking = false; this.actor.play('idle'); }
    }
  }

  /** Re-sync the sub-pixel position after an external teleport / cinematic walk. */
  sync(): void { this.fx = this.actor.x; this.fy = this.actor.y; }

  readInput(): MoveInput {
    let dx = 0, dy = 0;
    const k = this.keys;
    if (k) {
      if (k.A.isDown || k.LEFT.isDown) dx -= 1;
      if (k.D.isDown || k.RIGHT.isDown) dx += 1;
      if (k.W.isDown || k.UP.isDown) dy -= 1;
      if (k.S.isDown || k.DOWN.isDown) dy += 1;
    }
    if (dx === 0 && dy === 0) return this.touch;
    return { dx: Math.sign(dx) as MoveInput['dx'], dy: Math.sign(dy) as MoveInput['dy'] };
  }

  /** One fixed step. Returns true when the actor's integer position changed. */
  tick(dtMs: number): boolean {
    if (!this.enabled) return false;
    if (Math.round(this.fx) !== this.actor.x || Math.round(this.fy) !== this.actor.y) this.sync();
    const input = this.readInput();
    const r = stepMove(this.fx, this.fy, input, this.opts.speed, dtMs, this.isBlocked);
    this.fx = r.x;
    this.fy = r.y;
    if (r.facing && r.facing !== this.actor.facing) this.actor.setFacing(r.facing);
    this.actor.x = Math.round(r.x);
    this.actor.y = Math.round(r.y);
    const wantWalk = input.dx !== 0 || input.dy !== 0;
    if (wantWalk !== this.walking) { this.walking = wantWalk; this.actor.play(wantWalk ? 'walk' : 'idle'); }
    if (r.moved) {
      this.emitAcc += dtMs;
      if (this.emitAcc >= (this.opts.moveEmitMs ?? 250)) {
        this.emitAcc = 0;
        bus.emitTyped(Events.PLAYER_MOVED, { map: this.opts.mapId, x: this.actor.x, y: this.actor.y, facing: this.actor.facing });
      }
    }
    return r.moved;
  }

  destroy(): void {
    bus.offTyped(Events.INPUT_DIR, this.onDir);
    const kb = this.scene.input.keyboard;
    if (kb) {
      for (const ev of CONFIRM_KEYS) kb.off(ev, this.onConfirmKey);
      if (this.keys) for (const key of Object.values(this.keys)) kb.removeKey(key, true);
    }
    this.keys = null;
  }
}

/** Path-history trail: each follower walks the leader's recent path `spacingPx * (i + 1)` behind. */
export class DevFollowerTrail {
  private history: { x: number; y: number }[] = [];
  private floats: { x: number; y: number }[];
  private walking: boolean[];
  private lastLeader: { x: number; y: number };

  constructor(private readonly leader: IActor, private readonly followers: IActor[], private readonly opts: { spacingPx: number; speed: number; sampleEveryPx?: number }) {
    this.lastLeader = { x: leader.x, y: leader.y };
    this.floats = followers.map((f) => ({ x: f.x, y: f.y }));
    this.walking = followers.map(() => false);
    this.history.push({ x: leader.x, y: leader.y });
  }

  tick(dtMs: number): void {
    const sample = this.opts.sampleEveryPx ?? 4;
    const lx = this.leader.x, ly = this.leader.y;
    if (Math.abs(lx - this.lastLeader.x) + Math.abs(ly - this.lastLeader.y) >= sample) {
      this.history.push({ x: lx, y: ly });
      this.lastLeader = { x: lx, y: ly };
      const maxLen = Math.ceil((this.opts.spacingPx * (this.followers.length + 1)) / sample) + 4;
      if (this.history.length > maxLen) this.history.splice(0, this.history.length - maxLen);
    }
    this.followers.forEach((f, i) => {
      const back = Math.round((this.opts.spacingPx * (i + 1)) / sample);
      const target = this.history[Math.max(0, this.history.length - 1 - back)] ?? this.lastLeader;
      const p = this.floats[i];
      if (!p) return;
      const dx = target.x - p.x, dy = target.y - p.y;
      const dist = Math.hypot(dx, dy);
      const maxStep = (this.opts.speed * dtMs) / 1000;
      let moving = false;
      if (dist > 0.5) {
        const s = Math.min(dist, maxStep);
        p.x += (dx / dist) * s;
        p.y += (dy / dist) * s;
        moving = dist > 2;
        const face: Facing = facingFrom(dx, dy, f.facing);
        if (face !== f.facing) f.setFacing(face);
      }
      f.x = Math.round(p.x);
      f.y = Math.round(p.y);
      if (moving !== this.walking[i]) { this.walking[i] = moving; f.play(moving ? 'walk' : 'idle'); }
    });
  }

  destroy(): void { this.history = []; }
}
